import UnitConverter from "../../converters/unit_converter";

// The deviation IFC export lets a user choose the minimum deviation magnitude to
// include in the exported model. Historically this was hard-coded to 3 inches; the
// options below expose the full set of selectable thresholds. The pipeline
// (generate-batch-ifc) accepts the threshold as `minimum_deviation` in metres and
// defaults to 3" when the argument is absent, so we keep 3" as the default here.

export type DeviationToleranceOption = {
  /** Stable identifier used for option keys / test selectors. */
  key: string;
  /** Human-readable threshold label, e.g. `1/8"`, `3"`, `6"`. */
  label: string;
  /** Threshold magnitude in inches. */
  inches: number;
};

export const DEFAULT_DEVIATION_TOLERANCE_INCHES = 3;

export const DEVIATION_TOLERANCE_OPTIONS: DeviationToleranceOption[] = [
  { key: "one-eighth", label: `1/8"`, inches: 0.125 },
  { key: "one-quarter", label: `1/4"`, inches: 0.25 },
  { key: "one-half", label: `1/2"`, inches: 0.5 },
  { key: "one", label: `1"`, inches: 1 },
  { key: "two", label: `2"`, inches: 2 },
  { key: "three", label: `3"`, inches: 3 },
  { key: "six", label: `6"`, inches: 6 },
];

export const DEFAULT_DEVIATION_TOLERANCE_OPTION: DeviationToleranceOption =
  DEVIATION_TOLERANCE_OPTIONS.find(option => option.inches === DEFAULT_DEVIATION_TOLERANCE_INCHES);

export const toleranceOptionByKey = (key: string): DeviationToleranceOption =>
  DEVIATION_TOLERANCE_OPTIONS.find(option => option.key === key) ?? DEFAULT_DEVIATION_TOLERANCE_OPTION;

/** Label as shown on the threshold selector. */
export const toleranceDisplayLabel = (option: DeviationToleranceOption): string => option.label;

/** Convert a tolerance option's threshold to metres for the pipeline `minimum_deviation` argument. */
export const toleranceOptionToMetres = (option: DeviationToleranceOption): number =>
  UnitConverter.fromInchesToMeters(option.inches);

/** Spoken-form magnitude, e.g. `3 inches`, `1 inch`, `1/8"`, used in summary copy. */
const toleranceMagnitudeWords = (option: DeviationToleranceOption): string => {
  if (Number.isInteger(option.inches)) {
    return `${option.inches} ${option.inches === 1 ? "inch" : "inches"}`;
  }
  return option.label;
};

/** Affirmation copy shown under the tolerance selector, e.g. `Selected: deviations ≥ 3" will be included in this export`. */
export const toleranceAffirmationText = (option: DeviationToleranceOption): string =>
  `Selected: deviations ≥ ${option.label} will be included in this export`;

/** Affirmation copy shown under the tolerance selector, e.g. `Selected: deviations ≥ 3" will be included in this export`. */
export const toleranceNoDeviationsText = (option: DeviationToleranceOption): string =>
  `No PBEs deviate ≥ ${option.label} for the selected areas. Lower the tolerance or go back and choose different areas.`;

/**
 * As-built threshold copy. Every built element exports either way; the
 * threshold only decides which are placed where they are, not as designed.
 */
export const asBuiltToleranceAffirmationText = (option: DeviationToleranceOption): string =>
  `All BIM elements marked as built will be exported. BIM elements deviated ≥ ${option.label} and above will be exported in their as-built (deviated) location`;

/**
 * Shown when nothing deviates that far. Unlike the deviation export this is
 * no dead end: the export still runs, everything at its as-designed position.
 */
export const asBuiltToleranceNoDeviationsText = (option: DeviationToleranceOption): string =>
  `No deviations have been detected for tolerance ≥ ${option.label}. All elements will be exported in the as-designed location.`;

/**
 * Trade step empty state. A dead end unlike the tolerance warning, so it points
 * back at the area step rather than at the threshold.
 */
export const asBuiltNoBuiltTradesText =
  "No trades have built elements in the selected areas, so there is nothing to export. Go back and choose different areas.";

/** Compact summary shown on the review step, e.g. `≥ 3" (all deviations 3 inches and above)`. */
export const toleranceSummaryText = (option: DeviationToleranceOption): string =>
  `≥ ${option.label} (all deviations ${toleranceMagnitudeWords(option)} and above)`;

/** Filename-safe threshold token for the export filename, e.g. `3in`, `1-8in`. */
export const toleranceFileNameSlug = (option: DeviationToleranceOption): string =>
  `${option.label.replace(/"/g, "").replace(/\//g, "-")}in`;

/** True when the option matches the pipeline default and the argument can be omitted. */
export const isDefaultTolerance = (option: DeviationToleranceOption): boolean =>
  option.inches === DEFAULT_DEVIATION_TOLERANCE_INCHES;
